import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import SharedSummaryDetail, {
  type SharedSummaryStatus,
} from '@/components/Sessions/SharedSummaryDetail';
import { useView } from '@/hooks/useView';
import {
  buildMemberMomentsAnalyticsProperties,
  MEMBER_MOMENTS_EVENTS,
  momentsAnalytics,
} from '@/services/momentsAnalytics';
import { useGetSharedArtifactBySessionQuery } from '@/store/api/sharedArtifactApi';

const SessionSummaryPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOverview } = useView();
  const selfSessionsBase = pathname.startsWith('/coach/sessions/')
    ? '/coach/sessions'
    : '/m/sessions';
  const { memberSessionId, sessionId, id: memberId } = useParams();

  const resolvedSessionId = (memberSessionId ?? sessionId ?? '') as string;

  const {
    data: artifact,
    error,
    isLoading,
  } = useGetSharedArtifactBySessionQuery(
    { session_id: resolvedSessionId },
    { skip: !resolvedSessionId },
  );

  const onBack = () => {
    if (isOverview && memberSessionId && memberId) {
      navigate(`/coach/members/${memberId}/sessions/${resolvedSessionId}`);
      return;
    }

    navigate(`${selfSessionsBase}/${resolvedSessionId}`);
  };

  useEffect(() => {
    if (!artifact || memberId) {
      return;
    }
    momentsAnalytics.viewed(
      MEMBER_MOMENTS_EVENTS.viewedArtifact,
      'member_summary',
      window.location.pathname,
      buildMemberMomentsAnalyticsProperties({
        artifactId: artifact.moments_artifact_id,
        profileSlug: artifact.profile_slug,
        route: window.location.pathname,
        sessionId: resolvedSessionId,
        surface: 'artifact_detail',
      }),
      { dedupeKey: `summary:${resolvedSessionId}:${window.location.pathname}` },
    );
  }, [artifact, memberId, resolvedSessionId]);

  let status: SharedSummaryStatus = 'ready';
  if (isLoading) {
    status = 'loading';
  } else if (error) {
    status = 'error';
  } else if (!artifact) {
    status = 'empty';
  }

  return (
    <SharedSummaryDetail artifact={artifact} status={status} onBack={onBack} />
  );
};

export default SessionSummaryPage;
